import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { DialogContent, Grid, TextField } from "@mui/material";
import { toast } from "react-toastify";
import axios from "axios";
import { useTranslation } from "react-i18next";

//import constans
import BASE_URL from "../../../constants/baseUrls";

//import general components
import { BaseButton } from "../base-button/base-button";
import BaseDialog from "../base-dialog/base-dialog";
import { fixNumbers } from "../../../utils/utility";
import BaseTextField from "../base-text-field/base-text-field";

function UserInformationModal({ open, setOpen }) {
    //general variables
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const accessToken = localStorage.getItem("accessToken");
    const { t } = useTranslation();

    //handle close modal
    const handleClose = () => {
        setOpen(false);
        navigate(-1);
        searchParams.delete("action");
        setSearchParams(searchParams);
    };

    //initial form values
    const {
        register,
        handleSubmit,
        getValues,
        formState: { errors },
    } = useForm();

    const handleRegistration = async data => {
        try {
            const response = await axios.patch(`${BASE_URL}/api/v2/account/user/info/`, data, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });
            toast.success(t("editMessage"), { toastId: "custom-id-yes" });
            localStorage.setItem("userInformation", JSON.stringify(response.data.data));
        } catch (error) {
            if (error.response.status === 401) {
                handleClose();
                localStorage.clear();
                setSearchParams({ action: "login" });
            } else {
                toast.error(t("errorMessage"), { toastId: "custom-id-yes" });
            }
        } finally {
            handleClose();
        }
    };
    const handleError = errors => console.error(errors);

    const registerOptions = {
        first_name: {
            required: `${t("firstName")} ${t("isRequired")}`,
            minLength: { value: 2, message: t("firstNameValidation1") },
            pattern: { value: /^[\u0600-\u06FF\s]+$/, message: t("firstNameValidation2") },
        },
        last_name: {
            required: `${t("lastName")} ${t("isRequired")}`,
            minLength: { value: 2, message: t("lastNameValidation1") },
            pattern: { value: /^[\u0600-\u06FF\s]+$/, message: t("lastNameValidation2") },
        },
        // email: {
        //     required: `${t("email")} ${t("isRequired")}`,
        //     pattern: {
        //         value: /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,
        //         message: t("invalidEmail"),
        //     },
        // },
        national_code: {
            required: `${t("nationalCode")} ${t("isRequired")}`,
            validate: {
                validateNumber: nationalId => {
                    nationalId = fixNumbers(nationalId);
                    if (!nationalId) {
                        return true;
                    }

                    // Check length is 10
                    if (nationalId.length < 8 || 10 < nationalId.length) {
                        return `${t("nationalCode")} ${t("isWrong")}`;
                    }

                    //Check if all of the numbers are the same
                    if (
                        nationalId === "0000000000" ||
                        nationalId === "1111111111" ||
                        nationalId === "2222222222" ||
                        nationalId === "3333333333" ||
                        nationalId === "4444444444" ||
                        nationalId === "5555555555" ||
                        nationalId === "6666666666" ||
                        nationalId === "7777777777" ||
                        nationalId === "8888888888" ||
                        nationalId === "9999999999"
                    ) {
                        return `${t("nationalCode")} ${t("isWrong")}`;
                    }

                    // STEP 00 : if nationalId.length==8 add two zero on the left
                    if (nationalId.length < 10) {
                        let zeroNeeded = 10 - nationalId.length;
                        let zeroString = "0".repeat(zeroNeeded);
                        nationalId = zeroString + nationalId;
                    }

                    // STEP 1: Sum all numbers
                    let sum = 0;
                    for (let i = 0; i < 9; i++) {
                        sum += parseInt(nationalId.charAt(i)) * (10 - i);
                    }

                    // STEP 2: MOD ON 11
                    let mod = sum % 11;

                    // STEP 3: Check with 2
                    let finalValue = mod >= 2 ? 11 - mod : mod;

                    // STEP 4: Final Step check with control value
                    if (finalValue !== parseInt(nationalId.charAt(9))) {
                        return `${t("nationalCode")} ${t("isWrong")}`;
                    }

                    const foundNationalCode = getValues("national_code") === nationalId;
                    if (foundNationalCode && foundNationalCode.length > 1) {
                        return `${t("nationalCodeValidation")}`;
                    }

                    return true;
                },
            },
        },
    };

    return (
        <BaseDialog title={t("userDetails")} open={open} handleClose={handleClose} maxWidth="xs" top="auto" icon="close">
            <DialogContent dividers={true}>
                <Grid
                    component="form"
                    container
                    item
                    xs={12}
                    rowGap={3}
                    justifyContent="center"
                    onSubmit={handleSubmit(handleRegistration, handleError)}
                >
                    {[
                        { id: 1, label: t("firstName"), name: "first_name", type: "text", dir: "rtl" },
                        { id: 2, label: t("lastName"), name: "last_name", type: "text", dir: "rtl" },
                        // { id: 3, label: t("email"), name: "email", type: "text", dir: "ltr" },
                        { id: 3, label: t("nationalCode"), name: "national_code", type: "number", dir: "ltr" },
                    ].map(field => (
                        <Grid key={field?.id} container item xs={10}>
                            <BaseTextField
                                dir={field.dir}
                                label={field.label}
                                radius="10px"
                                name={field.name}
                                type={field.type}
                                focused={!!getValues(field.name)}
                                register={{
                                    ...register(`${field.name}`, registerOptions?.[field.name]),
                                }}
                                error={errors?.[field.name]?.message}
                            />
                        </Grid>
                    ))}
                    <Grid container item xs={12} md={5} justifyContent="flex-end">
                        <BaseButton text={t("submitInformation")} color="primary" size="medium" radius="radius-10" type="submit" />
                    </Grid>
                </Grid>
            </DialogContent>
        </BaseDialog>
    );
}

export default UserInformationModal;
